import type { BranchPermission, ModuleKey } from '../../../../shell/session/store/sessionTypes';
import type { InvitationPermissions } from '../../../application/employeeInvitations';
import {
  permissionTripleFromLevel,
  type PermissionLevel,
} from '../../../store/useEmployeesStore';
import { presetToApiPayload, type RolePreset } from './EmployeeRolePresets';

/** Convierte un preset de invitación en filas EBP-like para resumen UI. */
export function branchPermissionsFromPreset(
  preset: RolePreset,
  visibleModules: readonly ModuleKey[]
): BranchPermission[] {
  const payload = presetToApiPayload(preset, visibleModules);
  return visibleModules.map((module) => ({
    module,
    canView: payload[module]?.canView ?? false,
    canEdit: payload[module]?.canEdit ?? false,
    canVoid: payload[module]?.canVoid ?? false,
    canDiscount: payload[module]?.canDiscount ?? false,
    canManageAgenda: payload[module]?.canManageAgenda,
    canViewOthers: payload[module]?.canViewOthers ?? true,
    actions: payload[module]?.actions ?? {},
  }));
}

/** Payload de invitación listo para la Edge Function. */
export function branchPermissionsToInvitationPayload(
  perms: readonly BranchPermission[]
): InvitationPermissions {
  const out: InvitationPermissions = {};
  for (const p of perms) {
    out[p.module] = {
      canView: p.canView,
      canEdit: p.canEdit,
      canVoid: p.canVoid ?? false,
      canDiscount: p.canDiscount ?? false,
      ...(p.canManageAgenda !== undefined ? { canManageAgenda: p.canManageAgenda } : {}),
      ...(p.canViewOthers !== undefined ? { canViewOthers: p.canViewOthers } : {}),
      actions: { ...(p.actions ?? {}) },
    };
  }
  return out;
}

export function setModuleLevelInPermissions(
  perms: BranchPermission[],
  module: ModuleKey,
  level: PermissionLevel
): BranchPermission[] {
  return perms.map((p) => {
    if (p.module !== module) return p;
    const triple = permissionTripleFromLevel(level, {
      canManageAgenda: p.canManageAgenda,
      canViewOthers: p.canViewOthers,
      actions: p.actions,
    });
    return {
      ...p,
      ...triple,
      canManageAgenda: level === 'none' ? false : triple.canManageAgenda,
      canViewOthers: level === 'none' ? false : triple.canViewOthers,
    };
  });
}

function permSnapshot(p: BranchPermission): string {
  return [
    p.canView,
    p.canEdit,
    p.canVoid,
    p.canDiscount,
    p.canManageAgenda,
    p.canViewOthers,
    JSON.stringify(p.actions ?? {}),
  ].join('|');
}

/** True si el borrador difiere del preset base (rol). */
export function permissionsDifferFromPreset(
  draft: readonly BranchPermission[],
  preset: readonly BranchPermission[]
): boolean {
  const presetByModule = new Map(preset.map((p) => [p.module, p]));
  for (const row of draft) {
    const base = presetByModule.get(row.module);
    if (!base || permSnapshot(row) !== permSnapshot(base)) return true;
  }
  return false;
}
